/**
 * URL-persisted dashboard state.
 *
 * On mount: reads `?dash_state=<base64-json>` from the URL and seeds
 * the dashboard state store with the decoded payload (overrides
 * the spec's initial state — explicit URL wins).
 *
 * While running: subscribes to the state store, debounces by 300ms,
 * writes the latest snapshot back to the URL via `history.replaceState`
 * so reload / share preserves the filter set without spamming the
 * browser history.
 *
 * Hardening:
 *  - Encoded payload over `MAX_URL_BYTES` is dropped with a dev warning
 *    instead of a too-long URL.
 *  - Decoding errors silently fall back to the spec's initial state.
 *  - Server-side render is a no-op (no `window`).
 */

import { useEffect, useRef } from "react"

import { useDashboardStateStore } from "./dashboard-state"

export const DASH_STATE_PARAM = "dash_state"
export const URL_SYNC_DEBOUNCE_MS = 300
const MAX_URL_BYTES = 6_000

export interface UrlPersistedStateOptions {
  /** Disable both read and write. Useful in mini-previews. */
  enabled?: boolean
  /** Override the URL query-string parameter name. */
  paramName?: string
  /** Maximum encoded size in bytes; oversize payloads are dropped. */
  maxBytes?: number
  /** Override debounce window for tests. */
  debounceMs?: number
}

/** Encode a state snapshot into a URL-safe base64 string. */
export function encodeStateForUrl(state: Record<string, unknown>): string {
  const json = JSON.stringify(state)
  const utf8 = new TextEncoder().encode(json)
  // btoa requires Latin-1; we have UTF-8 bytes. Convert byte-by-byte.
  let bin = ""
  for (let i = 0; i < utf8.length; i++) bin += String.fromCharCode(utf8[i]!)
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

/** Decode a URL-encoded payload back into a state snapshot.
 *
 * @param encoded   Base64URL-encoded JSON object payload.
 * @param maxBytes  Optional cap on the encoded length, applied BEFORE
 *                  ``atob``. Defaults to {@link MAX_URL_BYTES}. Without
 *                  this cap the read path would happily decode (and
 *                  ``JSON.parse``) an arbitrarily large URL — most
 *                  browsers accept ~32k+ URL chars, base64 inflates 4×,
 *                  giving a JSON-bomb / nested-object DoS via
 *                  attacker-controlled share-URL. Passing
 *                  ``Number.POSITIVE_INFINITY`` disables the guard for
 *                  callers that have already bounded input size.
 */
export function decodeStateFromUrl(
  encoded: string,
  maxBytes: number = MAX_URL_BYTES
): Record<string, unknown> | null {
  if (encoded.length > maxBytes) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[dash] URL state exceeds ${maxBytes} bytes (got ${encoded.length}); ignoring.`)
    }
    return null
  }
  try {
    const padded =
      encoded.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (encoded.length % 4)) % 4)
    const bin = atob(padded)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
    const json = new TextDecoder().decode(bytes)
    const parsed = JSON.parse(json)
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
    if (Array.isArray(parsed) && process.env.NODE_ENV !== "production") {
      // Shareable-URL tooling tends to drift toward "just stringify the
      // whole state" without realising Dash's snapshot shape is
      // object-rooted. Surface the mismatch in dev so authors don't
      // chase a silent no-op.
      console.warn(
        "[dash] URL state decoded as an array, but Dash expects an object root — ignoring."
      )
    }
    return null
  } catch {
    return null
  }
}

/**
 * React hook variant for use inside a DashboardStateProvider subtree.
 *
 * `enabled` defaults to true. Disable by passing the option explicitly
 * (e.g. for the dashboard mini-preview, where we don't want to push
 * tiny preview state into the live URL).
 */
export function useUrlPersistedDashState(options: UrlPersistedStateOptions = {}): void {
  const {
    enabled = true,
    paramName = DASH_STATE_PARAM,
    maxBytes = MAX_URL_BYTES,
    debounceMs = URL_SYNC_DEBOUNCE_MS,
  } = options

  const store = useDashboardStateStore()
  const didLoadRef = useRef(false)

  // Read the URL once on mount and seed the store. ``maxBytes`` is
  // captured for the closure below.
  useEffect(() => {
    if (!enabled) return
    if (typeof window === "undefined") return
    if (didLoadRef.current) return
    didLoadRef.current = true
    try {
      const params = new URLSearchParams(window.location.search)
      const encoded = params.get(paramName)
      if (!encoded) return
      // Reuse the same maxBytes ceiling for read AND write so a payload
      // we'd refuse to write also can't be loaded from an external
      // share-URL.
      const decoded = decodeStateFromUrl(encoded, maxBytes)
      if (decoded) {
        store.initDashState(decoded)
      }
    } catch (err) {
      // Privacy mode / weird sandboxes throw DOMException on URLSearchParams
      // or location access — swallow those silently and let the spec's
      // initial state apply as if no URL were present. Anything else
      // (programmer error, library bug) deserves a console trail.
      if (!(err instanceof DOMException)) {
        console.error("[dash] URL state read failed", err)
      }
    }
  }, [enabled, paramName, store, maxBytes])

  // Subscribe + debounce + push to URL.
  useEffect(() => {
    if (!enabled) return
    if (typeof window === "undefined") return

    let timer: ReturnType<typeof setTimeout> | null = null

    const flush = () => {
      timer = null
      const snapshot = store.getSnapshot()
      try {
        const url = new URL(window.location.href)
        if (Object.keys(snapshot).length === 0) {
          url.searchParams.delete(paramName)
        } else {
          const encoded = encodeStateForUrl(snapshot)
          if (encoded.length > maxBytes) {
            // Don't write — but clear any stale param so a small state
            // change doesn't keep the previous huge value alive.
            url.searchParams.delete(paramName)
            if (process.env.NODE_ENV !== "production") {
              console.warn(
                `[dash] URL-persisted state exceeds ${maxBytes} bytes (got ${encoded.length}); skipping URL sync.`
              )
            }
          } else {
            url.searchParams.set(paramName, encoded)
          }
        }
        window.history.replaceState(window.history.state, "", url.toString())
      } catch (err) {
        // history.replaceState can throw SecurityError / DOMException
        // when the URL grows past per-browser limits or in privacy
        // sandboxes. Swallow those — the URL-persist feature is best-
        // effort. Anything else is a real bug; surface it.
        if (!(err instanceof DOMException)) {
          console.error("[dash] URL state write failed", err)
        }
      }
    }

    const unsub = store.subscribe(() => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(flush, debounceMs)
    })
    return () => {
      unsub()
      if (timer) clearTimeout(timer)
    }
  }, [enabled, paramName, store, maxBytes, debounceMs])
}
